/**
 * Onboarding completion — written in exactly ONE place.
 *
 * The tour ends through more than one door: finishing it, skipping it, and
 * (since the import step exists) choosing an import, which closes the modal
 * from `App.vue` instead of from the tour itself. Only the first two used to
 * record completion, so an importer's tour resurrected mid-sequence on the
 * next load — the modal reappeared at step 1 after the user had already told
 * us what they wanted. Both writes now go through here.
 *
 * The localStorage write is synchronous and happens first on purpose: a reload
 * one tick after the user closes the tour must never re-show it, even if the
 * backend call is still in flight or fails. The backend flag is what carries
 * completion to the user's other devices, so it is best-effort and never
 * rejects — a caller that closes a modal must not be able to be left holding
 * an unhandled rejection instead of a closed modal.
 */
import ApiService from '@/services/api.js'

export const ONBOARDING_KEY = 'linkflare_onboarding_v1'

/**
 * Records the tour as done, locally and (best-effort) on the backend.
 *
 * @param {() => Promise<string|null>} getAuthToken from `useLinkflareAuth()`
 * @returns {Promise<void>} resolves once the backend call settles; never rejects
 */
export function markOnboardingComplete(getAuthToken) {
  try {
    localStorage.setItem(ONBOARDING_KEY, 'completed')
  } catch (e) {
    // Private mode / blocked storage: the backend flag still covers this.
  }
  return Promise.resolve()
    .then(() => getAuthToken())
    .then(token => ApiService.markOnboardingCompleted(token))
    .then(() => undefined)
    .catch(e => { console.warn('Failed to persist onboarding state', e) })
}

/** True when this browser has already seen the tour through to the end. */
export function isOnboardingCompletedLocally() {
  try {
    return localStorage.getItem(ONBOARDING_KEY) === 'completed'
  } catch (e) {
    return false
  }
}
